import { Add, ArrowForward, DeleteOutline, HelpOutline } from '@mui/icons-material';
import { Box, Button, Divider, Grid2 as Grid, IconButton, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import uuid from 'react-uuid';
import { SCIMGroupMappingWrite } from 'api';
import { SCIM_STANDALONE_USER_PROVISIONING_TOOLTIP } from 'common/constants/firms';
import { ANALYST_VALUE, COMPANY_USER_VALUE, FUND_ADMIN_VALUE, USER_ROLES } from 'common/constants/user';
import { GroupMapping as GroupMappingType } from 'pages/Firms/hooks/useSCIMConfiguration';

const VALID_ROLES = Object.values(USER_ROLES).filter(
  role => ![FUND_ADMIN_VALUE, COMPANY_USER_VALUE].includes(role.value)
);

const GroupMapping = ({
  groupMappings = [],
  setGroupMappings,
}: {
  groupMappings: GroupMappingType[];
  setGroupMappings: React.Dispatch<React.SetStateAction<GroupMappingType[]>>;
}) => {
  const handleAddRow = () => {
    setGroupMappings(prevState => [...prevState, { display_name: '', role_mapped: ANALYST_VALUE, key: uuid() }]);
  };

  const handleRemoveRow = (index: number) => {
    setGroupMappings(prevState => prevState.filter((_, i) => i !== index));
  };

  const handleChange = (index: number, field: keyof SCIMGroupMappingWrite, value: string) => {
    const updated = [...groupMappings];
    if (field === 'role_mapped') {
      updated[index].role_mapped = Number(value);
    } else {
      updated[index].display_name = value;
    }
    setGroupMappings(updated);
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4">Group Mapping</Typography>
        <Tooltip
          title={SCIM_STANDALONE_USER_PROVISIONING_TOOLTIP}
          placement="right-end"
          arrow>
          <HelpOutline fontSize="small" color="action" sx={{ cursor: 'help' }} />
        </Tooltip>
      </Stack>
      <Typography variant="body2" sx={{ mb: 3 }}>
        Map your Identity Provider groups to our application roles.
      </Typography>
      {Boolean(groupMappings.length) && (
        <>
          <Grid container spacing={2} sx={{ mb: 1, display: { xs: 'none', sm: 'flex' } }}>
            <Grid size={5.5}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                GROUP NAME
              </Typography>
            </Grid>
            <Grid size={1} /> {/* Space for Arrow */}
            <Grid size={4.5}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                INTERNAL ROLE
              </Typography>
            </Grid>
            <Grid size={1} /> {/* Space for Delete */}
          </Grid>

          <Stack spacing={2}>
            {groupMappings.map((mapping, index) => (
              <Grid container spacing={2} key={mapping.key} alignItems="center">
                <Grid size={{ xs: 12, sm: 5.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Admins"
                    value={mapping.display_name}
                    onChange={e => handleChange(index, 'display_name', e.target.value)}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 1 }} sx={{ textAlign: 'center' }}>
                  <ArrowForward color="disabled" sx={{ display: { xs: 'none', sm: 'inline-block' } }} />
                </Grid>

                <Grid size={{ xs: 10, sm: 4.5 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={mapping.role_mapped}
                    onChange={e => handleChange(index, 'role_mapped', e.target.value)}>
                    {VALID_ROLES.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid size={{ xs: 2, sm: 1 }} sx={{ textAlign: 'right' }}>
                  <IconButton color="error" onClick={() => handleRemoveRow(index)}>
                    <DeleteOutline />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />
        </>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button startIcon={<Add />} onClick={handleAddRow} variant="text">
          Add Mapping
        </Button>
      </Box>
    </>
  );
};

export default GroupMapping;
